import React, { useState } from 'react';
import { Phone, Mail, Linkedin, Users, StickyNote, CalendarDays, ArrowRightLeft, MoreVertical, Pencil, Ban } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../ui/dropdown-menu';
import { fmtDate } from './crmApi';

/* CRM-FU-WORKSPACE-002 — premium chronological timeline (vertical rail + type icons). Same data/permissions as LeadTimeline. */
const ICONS = { Call: Phone, Email: Mail, LinkedIn: Linkedin, Meeting: Users, 'Conference/Event': CalendarDays, Note: StickyNote };
const cls = (t) => ({ Call: 't-call', Email: 't-email', LinkedIn: 't-linkedin', Meeting: 't-meeting', Note: 't-note' }[t] || 't-other');

export const statusOf = (a) => a.voided_at ? (a.superseded_by ? 'Superseded' : 'Voided') : a.completed_at ? 'Done' : 'Planned';
const chipCls = { Done: 'fuws-chip-done', Planned: 'fuws-chip-planned', Voided: 'fuws-chip-voided', Superseded: 'fuws-chip-voided', Overdue: 'fuws-chip-overdue', 'Due Today': 'fuws-chip-due', Upcoming: 'fuws-chip-upcoming' };
export const Chip = ({ value, testid }) => <span className={`fuws-chip ${chipCls[value] || 'fuws-chip-voided'}`} data-testid={testid}>{value}</span>;

export const FollowUpTimeline = ({ rows, canEdit, onEdit, onVoid, currentId, currentStatus, testid = 'fu-ws-timeline-list' }) => {
  if (!rows?.length) return <div className="px-5 py-8 text-sm text-slate-500 text-center" data-testid={`${testid}-empty`}>No activity yet.</div>;
  return (
    <div className="fuws-tl" data-testid={testid}>
      {rows.map((a, i) => {
        const cur = currentId && a.id === currentId;
        const conv = a.kind === 'conversion';
        const I = conv ? ArrowRightLeft : (ICONS[a.type] || StickyNote);
        const st = cur && !a.completed_at && !a.voided_at ? currentStatus : statusOf(a);
        return (
          <div key={a.id} style={{ '--i': i }}
            className={`fuws-tl-row fuws-tl-item ${cur ? 'is-current' : ''} ${a.voided_at ? 'is-voided' : ''} ${conv ? 'is-conversion' : ''}`}
            data-testid={cur ? 'timeline-current-row' : a.voided_at ? 'timeline-voided-row' : conv ? 'timeline-conversion-row' : 'timeline-row'} data-activity-id={a.id}>
            <span className={`fuws-tl-icon ${cur ? 't-current' : conv ? 't-meeting' : cls(a.type)}`}><I className="h-4 w-4" /></span>
            <div className="fuws-tl-body min-w-0">
              <div className="fuws-tl-when">{fmtDate(a.completed_at || a.scheduled_at)}{cur && <span className="fuws-chip fuws-chip-overdue ml-2 !py-0.5 !px-2 text-[10px]" data-testid="timeline-current-chip">Completing now</span>}</div>
              <div className="fuws-tl-type">{conv ? 'Converted' : a.type}</div>
              <div className="fuws-tl-note">{a.outcome || a.notes || '—'}
                {a.completed_via === 'mark_done' && !a.voided_at && <span className="ml-2 fuws-chip fuws-chip-done !py-0 !px-1.5 text-[10px]" title="Completed via Mark Done" data-testid="timeline-markdone-chip">Mark Done</span>}
                {a.edited_at && !a.voided_at && <span className="ml-2 fuws-chip fuws-chip-voided !py-0 !px-1.5 text-[10px]" title={`Edited by ${a.edited_by_name || '—'} · ${fmtDate(a.edited_at)}`} data-testid="timeline-edited-chip">Edited</span>}
              </div>
            </div>
            <div className="flex items-center gap-1 pt-0.5">
              <Chip value={st} testid={a.voided_at ? 'timeline-voided-chip' : undefined} />
              {canEdit?.(a) && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild><button type="button" className="fuws-icon-btn" aria-label="Activity actions" data-testid="timeline-menu-btn"><MoreVertical className="h-4 w-4" /></button></DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="min-w-[150px]">
                    <DropdownMenuItem onClick={() => onEdit?.(a)} data-testid="timeline-edit-btn"><Pencil className="h-3.5 w-3.5 mr-2" /> Edit activity</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => onVoid?.(a)} className="text-rose-700 focus:text-rose-700" data-testid="timeline-void-btn"><Ban className="h-3.5 w-3.5 mr-2" /> Void activity</DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export const useTimelineFilter = (rows) => {
  const [filter, setFilter] = useState('all');
  const when = (a) => new Date(a.completed_at || a.scheduled_at || a.created_at || 0).getTime();
  const shown = (rows || []).filter(a => filter === 'all' ? !a.voided_at : filter === 'done' ? !!a.completed_at && !a.voided_at : filter === 'planned' ? !a.completed_at && !a.voided_at : true).sort((x, y) => when(y) - when(x));
  return { filter, setFilter, shown };
};

export default FollowUpTimeline;
